import type React from "react";
import { useCallback, useState } from "react";
import type { FilterCriteria } from "@/features/graph/components/controls/FilterPanel";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";
import { COMMUNITY_COLORS } from "@/utils/graphAnalysis";

interface UseGraphFilterOptions {
	nodes: VisualNode[];
	edges: VisualEdge[];
	onFilterChange?: (filter: FilterCriteria) => void;
	onResetFilter?: () => void;
}

interface UseGraphFilterReturn {
	filteredNodes: VisualNode[];
	filteredEdges: VisualEdge[];
	setFilteredNodes: React.Dispatch<React.SetStateAction<VisualNode[]>>;
	setFilteredEdges: React.Dispatch<React.SetStateAction<VisualEdge[]>>;
	applyFilter: (filter: FilterCriteria) => void;
	resetFilter: () => void;
}

export const useGraphFilter = ({
	nodes,
	edges,
	onFilterChange,
	onResetFilter,
}: UseGraphFilterOptions): UseGraphFilterReturn => {
	const [filteredNodes, setFilteredNodes] = useState<VisualNode[]>(nodes);
	const [filteredEdges, setFilteredEdges] = useState<VisualEdge[]>(edges);

	const applyFilter = useCallback(
		(filter: FilterCriteria) => {
			onFilterChange?.(filter);
			if (!filter.itemType || !filter.property || !filter.value) {
				setFilteredNodes(nodes);
				setFilteredEdges(edges);
				return;
			}

			if (filter.itemType === "node") {
				const matchingNodes = nodes.filter((node) => {
					if (filter.property === "id") return node.id === filter.value;
					if (filter.property === "label") return node.name === filter.value;
					if (filter.property === "color") return node.color === filter.value;
					if (filter.property === "elementType") return node.elementType === filter.value;
					if (filter.property === "community") {
						const colorIndex = COMMUNITY_COLORS.indexOf(node.color || "");
						return colorIndex.toString() === filter.value;
					}
					return false;
				});

				const nodeIds = new Set(matchingNodes.map((node) => node.id));
				const matchingEdges = edges.filter((edge) => {
					const source = typeof edge.source === "string" ? edge.source : edge.source.id;
					const target = typeof edge.target === "string" ? edge.target : edge.target.id;
					return nodeIds.has(source) && nodeIds.has(target);
				});

				setFilteredNodes(matchingNodes);
				setFilteredEdges(matchingEdges);
				return;
			}

			const matchingEdges = edges.filter((edge) => {
				if (filter.property === "inferred") return (edge.inferred || false).toString() === filter.value;
				if (filter.property === "from") {
					const source = typeof edge.source === "string" ? edge.source : edge.source.id;
					return source === filter.value;
				}
				if (filter.property === "to") {
					const target = typeof edge.target === "string" ? edge.target : edge.target.id;
					return target === filter.value;
				}
				if (filter.property === "label") return edge.label === filter.value;
				return false;
			});

			const nodeIds = new Set<string>();
			matchingEdges.forEach((edge) => {
				const source = typeof edge.source === "string" ? edge.source : edge.source.id;
				const target = typeof edge.target === "string" ? edge.target : edge.target.id;
				nodeIds.add(source);
				nodeIds.add(target);
			});

			setFilteredNodes(nodes.filter((node) => nodeIds.has(node.id)));
			setFilteredEdges(matchingEdges);
		},
		[nodes, edges, onFilterChange],
	);

	const resetFilter = useCallback(() => {
		setFilteredNodes(nodes);
		setFilteredEdges(edges);
		onResetFilter?.();
	}, [nodes, edges, onResetFilter]);

	return {
		filteredNodes,
		filteredEdges,
		setFilteredNodes,
		setFilteredEdges,
		applyFilter,
		resetFilter,
	};
};

export default useGraphFilter;
